import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Shield, Zap, Globe, Coins, ArrowUpRight,
  CheckCircle, ChevronRight, Layers, Lock, RefreshCw, Banknote
} from 'lucide-react';

export default function DigitalAssetsPage() {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const displayFont = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = isAr ? 'var(--font-ar-body)' : 'var(--font-en-body)';

  const assetTypes = [
    {
      icon: '₿',
      name: 'Bitcoin (BTC)',
      desc: isAr ? 'أكبر عملة رقمية، سيولة عالية' : 'Largest cryptocurrency, high liquidity',
      role: isAr ? 'استشارات حول أفضل طريقة وتوقيت للتحويل' : 'Optimal conversion route and timing advisory',
    },
    {
      icon: 'Ξ',
      name: 'Ethereum (ETH)',
      desc: isAr ? 'رائدة العقود الذكية، سيولة عالية' : 'Leading smart contract token, highly liquid',
      role: isAr ? 'هيكلة التحويل لتقليل التكاليف ومخاطر التوقيت' : 'Structure conversion to minimise cost and timing risk',
    },
    {
      icon: '$',
      name: isAr ? 'العملات المستقرة (USDT/USDC)' : 'Stablecoins (USDT/USDC)',
      desc: isAr ? 'مربوطة بالدولار، تقلب سعري شبه معدوم' : 'USD-pegged, near-zero price volatility',
      role: isAr ? 'غالباً أنظف مسار لتحويل الأموال إلى درهم' : 'Often cleanest path to AED funds for property',
    },
    {
      icon: '◈',
      name: isAr ? 'الأصول المرمّزة / NFTs' : 'Tokenised RE / NFTs',
      desc: isAr ? 'رموز عقارية مسجلة على البلوكتشين' : 'Blockchain-recorded real estate tokens',
      role: isAr ? 'تقييم متخصص حسب كل حالة' : 'Case-by-case specialist evaluation required',
    },
  ];

  const steps = [
    {
      num: '01',
      title: isAr ? 'حدد العقار وأكّد نيتك' : 'Identify Your Property & Confirm Intent',
      desc: isAr ? 'اختر العقار المناسب وأكّد رغبتك في التمويل عبر العملات الرقمية. نبدأ بتقييم وضعك المالي الرقمي.' : 'Select your property and confirm your digital asset funding intention. We begin by assessing your digital financial position.',
      icon: Globe,
    },
    {
      num: '02',
      title: isAr ? 'قدّم تفاصيل عملاتك الرقمية' : 'Provide Digital Currency Details',
      desc: isAr ? 'جُليد تنصحك بالهيكل الأمثل: أي أصول تُحوَّل أولاً، وبأي قناة، ومتى.' : 'Guleed Mortgage advises on optimal structure: which assets, what order, which channel, and when.',
      icon: Layers,
    },
    {
      num: '03',
      title: isAr ? 'التحويل عبر بورصة مرخصة' : 'Conversion Through Regulated Exchange',
      desc: isAr ? 'تُحوَّل العملة الرقمية عبر قناة مؤسسية مرخصة. الأموال نظيفة وموثقة وقابلة للتتبع — شرط غير قابل للتفاوض في الإمارات.' : 'Digital currency converted via institutional/regulated route. Funds are clean, documented, and traceable — UAE non-negotiable requirement.',
      icon: Shield,
    },
    {
      num: '04',
      title: isAr ? 'أموال جاهزة بالدرهم للتسوية' : 'AED-Ready Funds for Property Settlement',
      desc: isAr ? 'جُليد تُسهّل عملية الشراء النقدي أو التمويل العقاري العادي، وتربطك بتسجيل دائرة الأراضي ونقل الملكية.' : 'Guleed Mortgage facilitates standard mortgage or cash purchase, connecting to DLD registration, NOC process, and title transfer.',
      icon: Banknote,
    },
  ];

  const values = [
    {
      icon: ArrowUpRight,
      title: isAr ? 'مسار واضح' : 'Clear Pathway',
      desc: isAr ? 'جسر بسيط من حيازات العملات الرقمية إلى تمويل العقار' : 'A simple bridge from crypto holdings to property funding',
    },
    {
      icon: RefreshCw,
      title: isAr ? 'تنسيق أفضل' : 'Better Coordination',
      desc: isAr ? 'وضوح أكبر في التوقيت والتحويل وخطوات التسوية' : 'More clarity on timing, conversion, and settlement steps',
    },
    {
      icon: Zap,
      title: isAr ? 'تنفيذ عملي' : 'Practical Execution',
      desc: isAr ? 'دعم للعملاء الذين يريدون استخدام العملات الرقمية كمصدر تمويل لشراء العقارات' : 'Support for clients who want to use digital currency as the source of funds for buying real estate',
    },
    {
      icon: Lock,
      title: isAr ? 'امتثال كامل' : 'Full Compliance',
      desc: isAr ? 'جميع التحويلات تتم عبر قنوات مرخصة ومتوافقة مع القوانين الإماراتية' : 'All conversions through regulated channels compliant with UAE laws',
    },
  ];

  return (
    <div data-testid="digital-assets-page">
      {/* Hero Section - Dark tech aesthetic */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#0A1628]">
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(42,82,190,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(42,82,190,0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
        </div>
        {/* Glow effects */}
        <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-[#2A52BE]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 end-1/3 w-80 h-80 bg-[#C9A84C]/8 rounded-full blur-[100px]" />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-8 py-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2A52BE]/15 border border-[#2A52BE]/30 mb-8">
              <Coins className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: bodyFont }}>
                {isAr ? 'خدمة حصرية في الإمارات' : 'Exclusive UAE Service'}
              </span>
            </div>

            <h1
              data-testid="da-hero-h1"
              className="text-3xl sm:text-4xl lg:text-[56px] font-bold text-white leading-tight mb-6"
              style={{ fontFamily: displayFont, lineHeight: isAr ? '1.4' : '1.12' }}
            >
              {isAr ? (
                <>نُحوّل <span className="text-[#C9A84C]">عملاتك الرقمية</span> إلى <span className="text-[#2A52BE]">تمويل عقاري</span></>
              ) : (
                <>We Turn <span className="text-[#C9A84C]">Digital Assets</span> Into <span className="text-[#2A52BE]">Property Funding</span></>
              )}
            </h1>

            <p
              className="text-base lg:text-lg text-white/60 mb-10 max-w-2xl leading-relaxed"
              style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.8' }}
            >
              {isAr
                ? 'جُليد للرهن العقاري تعمل كوسيط بين عملاتك الرقمية وشراء العقار. نساعدك في بيع أو تحويل أصولك الرقمية إلى أموال نقدية أو دراهم جاهزة للاستخدام في صفقات العقارات.'
                : 'Guleed Mortgages Real Estate LLC acts as a broker between your digital currency and the property purchase. We help you sell or convert digital assets into cash or AED-ready funds for real estate transactions.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="da-cta-primary"
                  className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0A1628] font-bold text-base px-8 py-6 rounded-lg group"
                >
                  {isAr ? 'ابدأ رحلتك الآن' : 'Start Your Journey'}
                  <ArrowRight className="w-5 h-5 ms-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-2 border-[#2A52BE]/40 text-white hover:bg-[#2A52BE]/10 font-semibold text-base px-8 py-6 rounded-lg"
                >
                  {isAr ? 'اكتشف خدماتنا' : 'Explore Services'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Digital Assets */}
      <section data-testid="da-what-section" className="bg-gradient-to-b from-[#0A1628] to-[#0B2F44] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#2A52BE] uppercase tracking-[0.2em] text-xs font-semibold mb-4" style={{ fontFamily: bodyFont }}>
              {isAr ? 'فهم الأصول الرقمية' : 'Understanding Digital Assets'}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: displayFont }}
            >
              {isAr ? 'ما هي الأصول الرقمية؟' : 'What Are Digital Assets?'}
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto text-base" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.7' }}>
              {isAr
                ? 'الأصول الرقمية هي عناصر ذات قيمة مخزنة إلكترونياً يمكن امتلاكها ونقلها وشراؤها وبيعها. كثير منها يستخدم تقنية البلوكتشين لتسجيل الملكية والتحقق من المعاملات.'
                : 'Digital assets are items of value stored electronically that can be owned, transferred, bought, and sold. Many use blockchain technology to record ownership and verify transactions.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {assetTypes.map((asset, i) => (
              <div
                key={i}
                data-testid={`da-asset-card-${i}`}
                className="relative bg-[#0D3A54]/60 backdrop-blur-sm rounded-2xl p-6 border border-[#2A52BE]/15 hover:border-[#C9A84C]/30 transition-all duration-300 group hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-[#2A52BE]/15 flex items-center justify-center mb-5 group-hover:bg-[#C9A84C]/15 transition-colors">
                  <span className="text-2xl font-bold text-[#C9A84C]" style={{ fontFamily: 'var(--font-en-mono)' }}>{asset.icon}</span>
                </div>
                <h3 className="font-bold text-white text-base mb-2">{asset.name}</h3>
                <p className="text-white/50 text-sm mb-4" style={{ lineHeight: isAr ? '1.8' : '1.6' }}>{asset.desc}</p>
                <div className="pt-3 border-t border-white/10">
                  <p className="text-[#C9A84C] text-xs font-medium" style={{ lineHeight: isAr ? '1.8' : '1.5' }}>
                    {isAr ? 'دور جُليد: ' : 'Guleed Mortgage Role: '}{asset.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Conversion Matters */}
      <section data-testid="da-why-section" className="bg-[#0B2F44] py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C9A84C] uppercase tracking-[0.2em] text-xs font-semibold mb-4" style={{ fontFamily: bodyFont }}>
                {isAr ? 'لماذا التحويل مهم' : 'Why Conversion Matters'}
              </p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6"
                style={{ fontFamily: displayFont }}
              >
                {isAr ? 'الجسر بين العملات الرقمية والعقارات' : 'Bridging Crypto to Real Estate'}
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-6" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.8' }}>
                {isAr
                  ? 'في دبي وأبوظبي، تسجيل العقارات والتسوية النهائية تتم عادةً بالدرهم الإماراتي. هذا يعني أن العملات الرقمية غالباً ما تحتاج إلى تحويل إلى الدرهم قبل إتمام الصفقة.'
                  : 'In Dubai and Abu Dhabi, property registration and final settlement are typically completed in UAE dirhams. This means crypto often needs to be converted into AED before completion.'}
              </p>
              <p className="text-white/60 text-base leading-relaxed mb-8" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.8' }}>
                {isAr
                  ? 'لهذا السبب، هيكلة الوساطة يمكن أن تساعد العميل في الانتقال من الأصول الرقمية إلى صيغة تسوية قابلة للاستخدام — بشكل منظم ومتوافق مع القوانين.'
                  : 'That is why a brokered structure can help you move from digital assets to a usable settlement format — organized and fully compliant with UAE regulations.'}
              </p>

              <div className="bg-[#071E2C] rounded-xl p-5 border border-[#C9A84C]/20">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#C9A84C] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#C9A84C] font-semibold text-sm mb-1">{isAr ? 'إشعار قانوني مهم' : 'Important Legal Notice'}</p>
                    <p className="text-white/50 text-xs leading-relaxed" style={{ lineHeight: isAr ? '1.8' : '1.6' }}>
                      {isAr
                        ? 'تسجيل العقارات في دبي وأبوظبي يتطلب قانونياً الدفع بالدرهم الإماراتي (AED). التحويل المباشر من العملات الرقمية إلى دائرة الأراضي غير مدعوم حالياً. جُليد تسد هذه الفجوة بشكل احترافي ومتوافق.'
                        : 'Property registration in Dubai and Abu Dhabi legally requires UAE Dirhams (AED). Direct crypto-to-DLD transfer is not currently supported. Guleed Mortgage bridges this gap professionally and compliantly.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual: AED conversion graphic */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0D3A54] to-[#071E2C] rounded-2xl p-10 border border-[#2A52BE]/20">
                <div className="text-center mb-8">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-2">{isAr ? 'عملة التسوية' : 'Settlement Currency'}</p>
                  <div className="text-6xl font-black text-white mb-2" style={{ fontFamily: 'var(--font-en-mono)' }}>AED</div>
                  <p className="text-white/50 text-sm">{isAr ? 'الدرهم الإماراتي' : 'UAE Dirham'}</p>
                </div>
                <div className="space-y-3">
                  {['BTC → AED', 'ETH → AED', 'USDT → AED', 'USDC → AED'].map((flow, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#0B2F44]/80 rounded-lg px-4 py-3 border border-[#2A52BE]/10">
                      <span className="text-white/70 text-sm font-mono">{flow}</span>
                      <CheckCircle className="w-4 h-4 text-[#1A7A4A]" />
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-white/10 text-center">
                  <p className="text-[#C9A84C] text-sm font-semibold">
                    {isAr ? 'عبر قنوات مرخصة ومتوافقة' : 'Via regulated & compliant channels'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Step Process */}
      <section data-testid="da-process-section" className="bg-[#071E2C] py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#2A52BE] uppercase tracking-[0.2em] text-xs font-semibold mb-4" style={{ fontFamily: bodyFont }}>
              {isAr ? 'كيف نعمل' : 'How It Works'}
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
              style={{ fontFamily: displayFont }}
            >
              {isAr ? 'عملية التحويل في 4 خطوات' : '4-Step Conversion Process'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {steps.map((step, i) => (
              <div
                key={i}
                data-testid={`da-step-${i}`}
                className="relative bg-[#0D3A54]/40 rounded-2xl p-8 border border-[#2A52BE]/15 hover:border-[#C9A84C]/25 transition-all duration-300 group"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2A52BE]/20 to-[#C9A84C]/10 flex items-center justify-center border border-[#2A52BE]/20 group-hover:border-[#C9A84C]/30 transition-colors">
                      <span className="text-2xl font-black text-[#C9A84C]" style={{ fontFamily: 'var(--font-en-mono)' }}>{step.num}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <step.icon className="w-4 h-4 text-[#2A52BE]" />
                      <h3 className="font-bold text-white text-base" style={{ fontFamily: displayFont }}>{step.title}</h3>
                    </div>
                    <p className="text-white/50 text-sm leading-relaxed" style={{ lineHeight: isAr ? '2' : '1.7' }}>
                      {step.desc}
                    </p>
                  </div>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute -bottom-4 start-8 z-10">
                    <ChevronRight className="w-5 h-5 text-[#2A52BE]/40 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value for Client */}
      <section data-testid="da-value-section" className="bg-[#0B2F44] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: displayFont }}
            >
              {isAr ? 'القيمة التي نقدمها لك' : 'Value for You'}
            </h2>
            <p className="text-white/50 max-w-xl mx-auto" style={{ fontFamily: bodyFont }}>
              {isAr ? 'لماذا تختار جُليد لتحويل عملاتك الرقمية إلى عقارات' : 'Why choose Guleed Mortgage to convert your crypto into property'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={i}
                data-testid={`da-value-card-${i}`}
                className="bg-[#0D3A54]/50 rounded-2xl p-7 border border-[#C9D6D9]/10 hover:border-[#C9A84C]/25 transition-all duration-300 hover:-translate-y-1 text-center group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#C9A84C]/20 transition-colors">
                  <v.icon className="w-7 h-7 text-[#C9A84C]" />
                </div>
                <h3 className="font-bold text-white text-base mb-2" style={{ fontFamily: displayFont }}>{v.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed" style={{ lineHeight: isAr ? '2' : '1.7' }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="da-cta-section" className="relative bg-[#071E2C] py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(201,168,76,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.4) 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }} />
        </div>
        <div className="absolute top-0 start-1/3 w-96 h-96 bg-[#C9A84C]/5 rounded-full blur-[150px]" />

        <div className="relative z-10 max-w-[800px] mx-auto px-6 lg:px-8 text-center">
          <Coins className="w-12 h-12 text-[#C9A84C] mx-auto mb-6" />
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: displayFont }}
          >
            {isAr ? 'مستعد لتحويل عملاتك الرقمية إلى عقار؟' : 'Ready to Convert Crypto Into Property?'}
          </h2>
          <p className="text-white/50 text-base mb-10 max-w-xl mx-auto" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.7' }}>
            {isAr
              ? 'تواصل مع فريقنا المتخصص للحصول على استشارة مجانية حول تحويل أصولك الرقمية إلى استثمار عقاري في الإمارات.'
              : 'Connect with our specialist team for a free consultation on converting your digital assets into UAE property investment.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#C9A84C] hover:bg-[#A68966] text-[#071E2C] font-bold text-base px-10 py-6 rounded-lg group">
                {isAr ? 'احجز استشارة مجانية' : 'Book Free Consultation'}
                <ArrowRight className="w-5 h-5 ms-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/rates">
              <Button variant="outline" className="border-2 border-white/20 text-white hover:bg-white/5 font-semibold text-base px-8 py-6 rounded-lg">
                {isAr ? 'اطلع على الأسعار' : 'View Rates'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
